import type { RequireAtLeastOne } from 'type-fest'
import type { ImageSourceSet } from '@velasca/types'
import { setQueryParams } from './setQueryParams'

interface Source {
  maxWidth: number
  imageWidth: number
}

/**
 * It takes a source URL and a list of sizes and returns a list of sources based on width.
 *
 * @param src - The original image URL.
 * @param sources - An array of objects that contain the width and dpr
 * of the image. The provided array cannot be empty.
 * @param defaultWidth - The default width image to use.
 */
export const buildWidthSources = (
  src: string,
  sources: RequireAtLeastOne<Source[], 0>,
  defaultWidth: number,
): ImageSourceSet => {
  const imageSourceSet = sources.map(({ imageWidth }) => {
    const imageUrl = setQueryParams(src, [
      { name: 'width', value: imageWidth.toString() },
    ])

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
    return { src: imageUrl.toString(), width: `${imageWidth.toString()}w` }
  })

  imageSourceSet.push({
    src: setQueryParams(src, [
      { name: 'width', value: defaultWidth.toString() },
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
    ]).toString(),
    width: `${defaultWidth.toString()}w`,
  })

  const sizes = [
    ...sources.map(({ maxWidth, imageWidth }) => {
      return `(max-width: ${maxWidth.toString()}px) ${imageWidth.toString()}px`
    }),
    `${defaultWidth.toString()}px`,
  ].join(', ')

  return { sources: imageSourceSet, sizes }
}
